import React, { useState, useEffect } from 'react';
import { Calendar, AlertCircle, Lock, Shield, Tent, UtensilsCrossed } from 'lucide-react';

export default function EventBlog() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('schedule');

  // Zelt state
  const [selectedDays, setSelectedDays] = useState([]);
  const [names, setNames] = useState('');
  const [tentSmall, setTentSmall] = useState(0);
  const [tentLarge, setTentLarge] = useState(0);
  const [freetext, setFreetext] = useState('');
  const [helpSetup, setHelpSetup] = useState(false);

  // Food sign-up state
  const [foodName, setFoodName] = useState('');
  const [foodItem, setFoodItem] = useState('');
  const [foodCategory, setFoodCategory] = useState('cake');
  const [foodList, setFoodList] = useState([]);

  const eventDays = [
    { label: "Do, 25.06.", value: "Donnerstag 25.06.", disabled: false },
    { label: "Fr, 26.06.", value: "Freitag 26.06.",   disabled: false },
    { label: "Sa, 27.06.", value: "Samstag 27.06.",   disabled: false },
    { label: "So, 28.06.", value: "Sonntag 28.06.",   disabled: true  },
  ];

  const toggleDay = (value, disabled) => {
    if (disabled) return;
    setSelectedDays(prev =>
      prev.includes(value) ? prev.filter(d => d !== value) : [...prev, value]
    );
  };

  const handleSubmit = () => {
    if (selectedDays.length === 0) return;
    const subject = "Zeltanmeldung Geburtstagsfestival";
    const body =
`Hey Masi & Dennis,

ich melde mich für eine Zeltübernachtung an!

Namen: ${names}
Übernachtungsnächte: ${selectedDays.join(', ')}
Beim Aufbau helfen: ${helpSetup ? 'Ja, ich bin dabei! 🙌' : 'Nein'}

Zelte:
  Klein (2–3 Personen): ${tentSmall}x
  Groß (>3 Personen):   ${tentLarge}x

${freetext ? `Sonstiges:\n${freetext}\n` : ''}
Liebe Grüße`;
    window.location.href = `mailto:dennisacc@dhilz.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const canSubmit = selectedDays.length > 0 && names.trim() !== '' && (tentSmall + tentLarge) > 0;

  const categoryLabel = (cat) => {
    if (cat === 'cake') return 'Kuchen/Süßes';
    if (cat === 'salad') return 'Salat/Herzhaftes';
    return 'Sonstiges';
  };

  const handleAddFood = () => {
    if (!foodName.trim() || !foodItem.trim()) return;
    setFoodList(prev => [...prev, {
      id: Date.now(),
      name: foodName.trim(),
      item: foodItem.trim(),
      category: foodCategory,
    }]);
    setFoodName('');
    setFoodItem('');
  };

  const handleRemoveFood = (id) => {
    setFoodList(prev => prev.filter(f => f.id !== id));
  };

  const handleFoodEmail = () => {
    if (foodList.length === 0) return;
    const lines = foodList.map(f => `- ${f.name}: ${f.item} (${categoryLabel(f.category)})`).join('\n');
    const subject = "Buffet-Anmeldung Geburtstagsfestival";
    const body = `Hey Masi & Dennis,\n\nfolgende Leute bringen am Samstag etwas fürs Buffet mit:\n\n${lines}\n\nLiebe Grüße`;
    window.location.href = `mailto:dennisacc@dhilz.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const canAddFood = foodName.trim() !== '' && foodItem.trim() !== '';

  const ACCESS_TOKEN = 'c4f9a7e3d8b2c6f1a5e9d4c8b3f7a2e6d1c5f9a4e8d3c7b2f6a1e5d9c4f8b3a7';
  const TOKEN_EXPIRY = null;

  useEffect(() => {
    validateAccess();
    document.title = "Masi & Dennis Geburtstagsfestival 🎉";
  }, []);

  const validateAccess = () => {
    setIsLoading(true);
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('access');
    if (!token) { setError('no_token'); setIsAuthenticated(false); setIsLoading(false); return; }
    const tokenFormat = /^[a-f0-9]{64}$/;
    if (!tokenFormat.test(token)) { setError('invalid_format'); setIsAuthenticated(false); setIsLoading(false); return; }
    if (token !== ACCESS_TOKEN) { setError('invalid_token'); setIsAuthenticated(false); setIsLoading(false); console.warn(`Unauthorized access attempt with token: ${token.substring(0, 8)}...`); return; }
    if (TOKEN_EXPIRY) {
      const expiry = new Date(TOKEN_EXPIRY);
      const now = new Date();
      if (now > expiry) { setError('expired'); setIsAuthenticated(false); setIsLoading(false); return; }
    }
    setIsAuthenticated(true);
    setError('');
    setIsLoading(false);
    sessionStorage.setItem('eventAccess', token);
  };

  const eventData = {
    title: "Masi & Dennis Geburtstagsfestival",
    schedule: [
      {
        day: "Tag 1 - Donnerstag", date: "25.06.2026",
        items: [
          { time: "14:00", activity: "Aufbau", location: "Grillplatz / Basecamp" },
          { time: "18:00", activity: "Musik & Lagerfeuer / Pizza & Bier für Helfer", location: "Grillplatz / Basecamp" }
        ]
      },
      {
        day: "Tag 2 - Freitag", date: "26.06.2026",
        items: [
          { time: "10:00", activity: "Frühstück", location: "Zeltplatz / Basecamp" },
          { time: "12:00", activity: "Los Spielos - Wer verliert, spült!", location: "Zeltplatz / Basecamp" },
          { time: "18:00", activity: "Karaoke - Das Mikro lügt nicht.", location: "Center Stage / Basecamp" },
          { time: "@Sunset", activity: "Movie Night", location: "Outdoor Kino / Basecamp" },
        ]
      },
      {
        day: "Tag 3 - Samstag", date: "27.06.2026",
        items: [
          { time: "10:00", activity: "Frühstück", location: "Zeltplatz / Grillplatz" },
          { time: "11:00", activity: "Wanderlust", location: "Wald" },
          { time: "14:00", activity: "Kaffe & Kuchen", location: "Grillplatz / Basecamp" },
          { time: "19:00", activity: "Abendbuffet", location: "Grillplatz / Basecamp" },
          { time: "Ready?", activity: "Party!", location: "Basecamp" },
        ]
      },
      {
        day: "Tag 4 - Sonntag", date: "28.06.2026",
        items: [
          { time: "10:00", activity: "Frühstück", location: "Zeltplatz / Grillplatz" },
          { time: "13:00", activity: "Abbau", location: "Basecamp" }
        ]
      }
    ],
    importantNotes: [
      "Übernachtungen vor Ort sind gerne gesehen, sind jedoch lediglich mit Zelt möglich",
      "Da die Anzahl der Zeltplätze vor Ort begrenzt ist, gebt uns bitte HIER Bescheid, falls ihr vor habt bei uns zu zelten",
      "Für unsere Übernachtungsgäste werden wir jeden Morgen entsprechend Frühstück stellen und Getränke sind generell von uns bereitgestellt",
      "Essen wird grundsätzlich gestellt, allerdings würden wir Samstag Abend ein Buffet machen und freuen uns über Salate und Fingerfood",
      "Für mitgebrachtes Essen & Getränke gibt es Kühlmöglickeiten",
      "Parkmöglichkeiten im Umkreis werden hier in den nächsten Wochen bekanntgegeben und auf einer Karte eingezeichnet"
    ]
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-indigo-600 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
          <div className="flex justify-center mb-6">
            <Lock className="w-16 h-16 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-center text-gray-800 mb-2">Du kommst hier nicht rein</h1>
          <p className="text-center text-gray-600 mb-6">Persönliches Pech</p>
        </div>
      </div>
    );
  }

  const cakes  = foodList.filter(f => f.category === 'cake');
  const salads = foodList.filter(f => f.category === 'salad');
  const others = foodList.filter(f => f.category === 'other');

  const tabs = [
    { id: 'schedule', label: 'Schedule',       icon: <Calendar className="w-4 h-4" /> },
    { id: 'zelt',     label: 'Zeltanmeldung',  icon: <Tent className="w-4 h-4" /> },
    { id: 'buffet',   label: 'Buffet',          icon: <UtensilsCrossed className="w-4 h-4" /> },
    { id: 'notes',    label: 'Infos',           icon: <AlertCircle className="w-4 h-4" /> },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-green-50 border-b border-green-100 py-2">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-center gap-2 text-sm text-green-700">
          <Shield className="w-4 h-4" />
          <span>Yeeey, du bist eingeladen</span>
        </div>
      </div>

      <header className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-8 px-4 shadow-lg">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-2xl md:text-4xl font-bold mb-2">{eventData.title}</h1>
        </div>
      </header>

      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 overflow-x-auto">
          <nav className="flex min-w-max md:min-w-0">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-4 border-b-2 font-medium transition-colors flex items-center gap-2 text-sm ${
                  activeTab === tab.id
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-8">

        {/* Schedule */}
        {activeTab === 'schedule' && (
          <div className="space-y-6">
            {eventData.schedule.map((day, idx) => (
              <div key={idx} className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-1">{day.day}</h2>
                <p className="text-gray-600 mb-4">{day.date}</p>
                <div className="space-y-3">
                  {day.items.map((item, itemIdx) => (
                    <div key={itemIdx} className="flex gap-4 pb-3 border-b border-gray-100 last:border-0">
                      <div className="font-semibold text-indigo-600 min-w-16">{item.time}</div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-800">{item.activity}</div>
                        <div className="text-sm text-gray-500">{item.location}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Zeltanmeldung */}
        {activeTab === 'zelt' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-1">Zeltanmeldung 🏕️</h2>
            <p className="text-gray-500 text-sm mb-6">Gebt uns kurz Bescheid, damit wir Platz einplanen können.</p>

            <div className="mb-6">
              <p className="font-semibold text-gray-700 mb-2">Namen der Übernachtungsgäste</p>
              <input
                type="text"
                value={names}
                onChange={(e) => setNames(e.target.value)}
                placeholder="z.B. Anna, Ben, Clara"
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-300"
              />
            </div>

            <div className="mb-6">
              <p className="font-semibold text-gray-700 mb-3">Welche Nächte? <span className="font-normal text-gray-400 text-sm">(Nacht = schlafen bis zum nächsten Morgen)</span></p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {eventDays.filter(d => !d.disabled).map((day) => (
                  <button
                    key={day.value}
                    onClick={() => toggleDay(day.value, day.disabled)}
                    className={`py-3 px-4 rounded-lg border-2 font-medium text-sm transition-colors ${
                      selectedDays.includes(day.value)
                        ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <p className="font-semibold text-gray-700 mb-3">Anzahl Zelte</p>
              <div className="space-y-3">
                {[
                  { label: 'Klein', sub: '(2–3 Personen)', val: tentSmall, set: setTentSmall },
                  { label: 'Groß',  sub: '(>3 Personen)',  val: tentLarge, set: setTentLarge },
                ].map(({ label, sub, val, set }) => (
                  <div key={label} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{label} <span className="text-gray-400">{sub}</span></span>
                    <div className="flex items-center gap-3">
                      <button onClick={() => set(v => Math.max(0, v - 1))} className="w-9 h-9 rounded-full border-2 border-gray-200 text-gray-600 font-bold hover:border-indigo-400 transition-colors">−</button>
                      <span className="text-lg font-bold text-gray-800 w-5 text-center">{val}</span>
                      <button onClick={() => set(v => v + 1)} className="w-9 h-9 rounded-full border-2 border-gray-200 text-gray-600 font-bold hover:border-indigo-400 transition-colors">+</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <p className="font-semibold text-gray-700 mb-3">Aufbau <span className="font-normal text-gray-400 text-sm">(Do, 25.06. ab 14:00)</span></p>
              <button
                onClick={() => setHelpSetup(h => !h)}
                className={`flex items-center gap-3 w-full p-3 rounded-lg border-2 font-medium text-sm transition-colors ${
                  helpSetup ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                <span className="text-lg">{helpSetup ? '✅' : '🔲'}</span>
                Ich helfe beim Aufbau!
              </button>
            </div>

            <div className="mb-6">
              <p className="font-semibold text-gray-700 mb-2">Sonstiges <span className="font-normal text-gray-400">(optional)</span></p>
              <textarea
                value={freetext}
                onChange={(e) => setFreetext(e.target.value)}
                placeholder="z.B. Fragen, Anmerkungen..."
                rows={3}
                className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-300 resize-none"
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={`w-full py-3 rounded-lg font-semibold text-white transition-colors ${
                canSubmit ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              Anmeldung abschicken ✉️
            </button>
            {!canSubmit && (
              <p className="text-xs text-gray-400 text-center mt-2">
                Bitte Namen, mind. eine Nacht und mind. ein Zelt angeben
              </p>
            )}
          </div>
        )}

        {/* Buffet */}
        {activeTab === 'buffet' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-1">Buffet-Anmeldung 🍰🥗</h2>
            <p className="text-gray-500 text-sm mb-6">
              Wer bringt am <span className="font-medium text-gray-700">Samstag Abend</span> etwas fürs Buffet mit?
              Einfach Namen, Kategorie und Gericht eintragen!
            </p>

            <div className="flex flex-col gap-3 mb-4">
              <div className="flex flex-col md:flex-row gap-3">
                <input
                  type="text"
                  value={foodName}
                  onChange={(e) => setFoodName(e.target.value)}
                  placeholder="Dein Name"
                  className="flex-1 border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                />
                <input
                  type="text"
                  value={foodItem}
                  onChange={(e) => setFoodItem(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && canAddFood && handleAddFood()}
                  placeholder={
                    foodCategory === 'cake'  ? 'z.B. Schokoladenkuchen' :
                    foodCategory === 'salad' ? 'z.B. Nudelsalat' :
                    'z.B. Baguette, Dips...'
                  }
                  className="flex-1 border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                {[
                  { id: 'cake',  emoji: '🍰', label: 'Kuchen',    active: 'border-pink-400 bg-pink-50 text-pink-700' },
                  { id: 'salad', emoji: '🥗', label: 'Salat',     active: 'border-green-400 bg-green-50 text-green-700' },
                  { id: 'other', emoji: '🍽️', label: 'Sonstiges', active: 'border-indigo-400 bg-indigo-50 text-indigo-700' },
                ].map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setFoodCategory(cat.id)}
                    className={`flex-1 py-3 px-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                      foodCategory === cat.id ? cat.active : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    {cat.emoji} {cat.label}
                  </button>
                ))}
                <button
                  onClick={handleAddFood}
                  disabled={!canAddFood}
                  className={`px-6 py-3 rounded-lg font-semibold text-sm transition-colors ${
                    canAddFood ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  + Eintragen
                </button>
              </div>
            </div>

            {foodList.length > 0 ? (
              <div className="mt-6 space-y-5">
                {[
                  { key: 'cake',  items: cakes,  label: '🍰 Kuchen & Süßes',       color: 'text-pink-500',   bg: 'bg-pink-50 border-pink-100' },
                  { key: 'salad', items: salads, label: '🥗 Salate & Herzhaftes',  color: 'text-green-600',  bg: 'bg-green-50 border-green-100' },
                  { key: 'other', items: others, label: '🍽️ Sonstiges',            color: 'text-indigo-500', bg: 'bg-indigo-50 border-indigo-100' },
                ].map(group => group.items.length > 0 && (
                  <div key={group.key}>
                    <p className={`text-xs font-semibold uppercase tracking-wider ${group.color} mb-2`}>{group.label}</p>
                    <div className="space-y-2">
                      {group.items.map(f => (
                        <div key={f.id} className={`flex items-center justify-between ${group.bg} border rounded-lg px-4 py-2.5`}>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-gray-800">{f.name}</span>
                            <span className="text-gray-400 text-sm">·</span>
                            <span className="text-sm text-gray-600">{f.item}</span>
                          </div>
                          <button
                            onClick={() => handleRemoveFood(f.id)}
                            className="text-gray-300 hover:text-red-400 transition-colors text-lg leading-none"
                            title="Entfernen"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <button
                  onClick={handleFoodEmail}
                  className="w-full py-3 rounded-lg font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
                >
                  Liste abschicken ✉️
                </button>
                <p className="text-xs text-gray-400 text-center -mt-2">
                  Öffnet deine Mail-App mit der kompletten Liste
                </p>
              </div>
            ) : (
              <div className="mt-4 py-8 text-center text-gray-400 border border-dashed border-gray-200 rounded-lg text-sm">
                Noch niemand eingetragen — sei der Erste! 🎉
              </div>
            )}
          </div>
        )}

        {/* Infos */}
        {activeTab === 'notes' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Wichtige Infos</h2>
              <ul className="space-y-3">
                {eventData.importantNotes.map((note, idx) => (
                  <li key={idx} className="flex gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">
                      {idx === 1
                        ? <>
                            Da die Anzahl der Zeltplätze vor Ort begrenzt ist, gebt uns bitte{' '}
                            <button
                              onClick={() => setActiveTab('zelt')}
                              className="font-bold text-indigo-600 underline underline-offset-2 hover:text-indigo-800"
                            >
                              HIER
                            </button>
                            {' '}Bescheid, falls ihr vor habt bei uns zu zelten
                          </>
                        : idx === 3
                        ? <>
                            Essen wird grundsätzlich gestellt, allerdings würden wir Samstag Abend ein Buffet machen und freuen uns über Salate und Fingerfood —{' '}
                            <button
                              onClick={() => setActiveTab('buffet')}
                              className="font-bold text-indigo-600 underline underline-offset-2 hover:text-indigo-800"
                            >
                              HIER
                            </button>
                            {' '}eintragen!
                          </>
                        : note
                      }
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Anfahrt</h2>

              <div className="mb-4 flex items-center gap-2 text-gray-600">
                <span>📍</span>
                <span className="font-medium">Frankfurter Str. 21A, 65529 Waldems</span>
              </div>

              <div className="mb-6">
                <h3 className="font-semibold text-gray-700 mb-3">🚗 Parken</h3>
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800 mb-3">
                  ⚠️ Zufahrt am Grillplatz nur zum Abladen — keine Parkmöglichkeiten vor Ort!
                </div>
                <div className="space-y-3">
                  <div className="flex gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <span className="text-lg">🅿️</span>
                    <div>
                      <p className="font-medium text-gray-800 text-sm">Empfehlung: Parkplatz am Spielplatz / Bolzplatz</p>
                      <p className="text-gray-600 text-sm mt-0.5">Kostenlos & ruhig, nahe Kreuzung B275/B8 — ca. 600m (~8 Min. zu Fuß) zum Grillplatz entlang der Frankfurter Straße.</p>
                    </div>
                  </div>
                  <div className="flex gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <span className="text-lg">🅿️</span>
                    <div>
                      <p className="font-medium text-gray-800 text-sm">Alternativ: Schwalbacher Straße / Schulgasse</p>
                      <p className="text-gray-600 text-sm mt-0.5">Wohngebiet im Ortskern — ebenfalls kostenlos, ca. 10–12 Min. zu Fuß.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="font-semibold text-gray-700 mb-3">🚆 Mit Bahn & Bus</h3>
                <div className="space-y-3">
                  <div className="flex gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <span className="text-lg">🚆</span>
                    <div>
                      <p className="font-medium text-gray-800 text-sm">RB21 / RB22 → Bahnhof Idstein</p>
                      <p className="text-gray-600 text-sm mt-0.5">Ab Frankfurt Hbf oder Wiesbaden — stündlich, täglich, ca. 37 Min. ab Frankfurt.</p>
                    </div>
                  </div>
                  <div className="flex gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <span className="text-lg">🚌</span>
                    <div>
                      <p className="font-medium text-gray-800 text-sm">Bus 231 → Haltestelle Esch</p>
                      <p className="text-gray-600 text-sm mt-0.5">Ab Idstein Bahnhof direkt nach Waldems-Esch. Von der Haltestelle ~5–10 Min. zu Fuß.</p>
                    </div>
                  </div>
                  <div className="flex gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <span className="text-lg">⚠️</span>
                    <div>
                      <p className="font-medium text-amber-800 text-sm">Fahrplan vorher prüfen!</p>
                      <p className="text-amber-700 text-sm mt-0.5">Der Bus 231 fährt am Wochenende sehr selten. Bitte vorab auf <a href="https://www.rmv.de" target="_blank" rel="noreferrer" className="underline font-medium">rmv.de</a> prüfen — oder Taxi ab Idstein einplanen (~10–15€).</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="w-full h-96 rounded-lg overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!4v1766333505356!6m8!1m7!1shDZaanVjrUz1OTasoM9gtg!2m2!1d50.24974991212381!2d8.32306927806708!3f111.08946119625165!4f-9.639622963896102!5f0.7820865974627469"
                  width="100%" height="100%"
                  style={{ border: 0 }}
                  allowFullScreen loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Event Location"
                ></iframe>
              </div>
            </div>
          </div>
        )}

      </main>

      <footer className="bg-white border-t border-gray-200 mt-12 py-6">
        <div className="max-w-4xl mx-auto px-4 text-center text-gray-600 text-sm">
          <p>Bei Fragen gerne jederzeit bei Masi oder Dennis melden</p>
        </div>
      </footer>
    </div>
  );
}